import React, { useLayoutEffect, useRef } from 'react';

import {
  CALL_FRAME_ATTRIBUTE,
  CALL_MIN_VARIABLE,
  DOCK_BAR_VARIABLE,
  callControlClip,
  dockBarHeight,
  hasCallControl,
  reservedCallHeight,
} from 'components/Alpha/LiveAssist/liveAssistFit';
import { useLiveAssistFrameStyles } from 'components/Alpha/LiveAssist/liveAssistStyles';

interface Props {
  children: React.ReactNode;
}

const LiveAssistFrame: React.FC<Props> = ({ children }) => {
  const classes = useLiveAssistFrameStyles();
  const frameRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (frame === null) return undefined;
    let reserved = 0;

    const fit = (): void => {
      const callView = frame.firstElementChild;
      if (!(callView instanceof HTMLElement)) return;
      const bar = dockBarHeight(callView.nextElementSibling);
      frame.style.setProperty(DOCK_BAR_VARIABLE, `${bar}px`);
      const inCall = hasCallControl(callView);
      frame.toggleAttribute(CALL_FRAME_ATTRIBUTE, inCall);
      if (!inCall) {
        if (reserved !== 0) {
          reserved = 0;
          frame.style.removeProperty(CALL_MIN_VARIABLE);
        }
        return;
      }
      if (reserved !== 0) frame.style.removeProperty(CALL_MIN_VARIABLE);
      const clip = callControlClip(callView);
      reserved =
        clip <= 1
          ? 0
          : reservedCallHeight(
              callView.getBoundingClientRect().height,
              clip,
              frame.clientHeight,
              bar,
            );
      if (reserved !== 0) frame.style.setProperty(CALL_MIN_VARIABLE, `${reserved}px`);
    };

    const callMutations = new MutationObserver(fit);
    const resize = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(fit);
    const watch = (): void => {
      callMutations.disconnect();
      resize?.disconnect();
      resize?.observe(frame);
      const callView = frame.firstElementChild;
      if (callView === null) return;
      callMutations.observe(callView, { childList: true, subtree: true });
      resize?.observe(callView);
    };
    const children = new MutationObserver(() => {
      watch();
      fit();
    });

    watch();
    fit();
    children.observe(frame, { childList: true });
    return () => {
      children.disconnect();
      callMutations.disconnect();
      resize?.disconnect();
    };
  }, []);

  return (
    <div ref={frameRef} className={classes.frame} data-testid="alpha-live-assist-frame">
      {children}
    </div>
  );
};

export default LiveAssistFrame;
